// KontaktmoeglichkeitPopup.tsx — Refined-natives K-Möglichkeit-Modal für die
// Kontakttabelle. Zwei blau hervorgehobene Icons (Telefon/E-Mail) in der Zelle;
// ein Klick öffnet ein echtes Modal (Scrim `.kvp-rd-scrim` + `.dm`, portaliert),
// mit Reitern Telefon | E-Mail. Aufbau je nach Kontakttyp:
//   • Firma:  „Firma / Zentrale" (eigene Nummern) + „Menschen fürs Unternehmen"
//             (verknüpfte Personen: Name, Verantwortlichkeit, intern/extern,
//             Sprung-Button zum Kontakt).
//   • Person: nur die eigenen Kontaktdaten (keine „Menschen fürs Unternehmen").
// Oben rechts: kleine, klickbare Social-Icons (nur gesetzte). Erreichbarkeit
// erscheint als eigene Zeile mit Uhr-Icon. Mobil-Nummern bekommen ein
// Smartphone-Icon. Da die Tabellen-Liste die Slim-Shape lädt, lädt das Modal
// beim Öffnen das volle Kontakt-Detail per `include=relations,relations-deep` nach.

import { useEffect, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import { SocialIcon } from "react-social-icons";
import { I } from "./kvpIcons";
import { apiFetch } from "../../utils/apiFetch";
import { mapContactRow } from "../../utils/contactRowMapper";
import { normalizeWebUrl } from "../../utils/webUrl";
import { useUnternehmensBegriff, verantwortungTitel } from "../../utils/unternehmensBegriff";
import { ASSISTENZ_ROLLE } from "../../data/privateAssistenz";
import { useTelephony } from "../../context/TelephonyContext";
import type { ContactDetailsFormValues } from "../../types/Contact";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";
import { useMailSchreiben } from "../mail/useMailSchreiben";

type Row = ContactDetailsFormValues;
type Mode = "phone" | "email";

const TYPE_LABEL: Record<string, string> = {
  phone: "Telefon",
  mobile: "Mobil",
  fax: "Fax",
  email: "E-Mail",
};

const SOCIAL_LABEL: Record<string, string> = {
  linkedin: "LinkedIn",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  xing: "Xing",
  youtube: "YouTube",
  homepage: "Webseite",
};

/** Icon je Kommunikationstyp (Mobil → Smartphone, sonst Telefon/Mail). */
const commIcon = (type?: string): ReactNode =>
  type === "mobile" ? I.smartphone : type === "email" ? I.mail : I.phone;

/** Vom Mapper fabrizierte Default-Labels ("Tel 1"/"Email 2") nicht als Bezeichnung zeigen. */
const realBez = (label?: string): string => {
  const l = (label ?? "").trim();
  if (!l || /^Tel \d+$/i.test(l) || /^Email \d+$/i.test(l)) return "";
  return l;
};

const XIcon = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);

/**
 * Eigene Kommunikations-Zeile (Telefon/Mobil/E-Mail) — immer ein Knopf.
 * Telefon löst app-weit callNumber() ans SDK-Widget aus (kein tel:-Link mehr),
 * E-Mail öffnet das KMVS-Mailfenster (kein mailto:-Link mehr, T-227/T-296).
 */
function CommRow({
  onClick,
  kind,
  bez,
  value,
  icon,
}: {
  onClick: () => void;
  kind: string;
  bez?: string;
  value: string;
  icon: ReactNode;
}) {
  const inner = (
    <>
      <span className="km-ic">{icon}</span>
      <span className="km-meta">
        <span className="km-label">
          {kind}
          {bez ? <span className="km-rel"> · {bez}</span> : null}
        </span>
        <span className="km-val">{value}</span>
      </span>
    </>
  );
  return (
    <button type="button" className="km-row" onClick={onClick}>
      {inner}
    </button>
  );
}

/** Erreichbarkeits-Zeile (Uhr-Icon) — reine Anzeige, kein Link. */
function ErreichbarkeitRow({ text }: { text: string }) {
  return (
    <div className="km-row km-static">
      <span className="km-ic km-ic-clock">{I.clock}</span>
      <span className="km-meta">
        <span className="km-label">Erreichbarkeit</span>
        <span className="km-val">{text}</span>
      </span>
    </div>
  );
}

/**
 * „Mensch fürs Unternehmen"-Zeile: Name + Verantwortlichkeit + intern/extern +
 * Sprung-Button. Der Wert ist ein Knopf: anrufen bzw. Mail im KMVS-Fenster.
 */
function ApRow({
  onClick,
  icon,
  name,
  resp,
  isExtern,
  value,
  onGo,
}: {
  onClick: () => void;
  icon: ReactNode;
  name: string;
  resp?: string;
  isExtern?: boolean;
  value: string;
  onGo: () => void;
}) {
  return (
    <div className="km-row km-ap">
      <span className="km-ic">{icon}</span>
      <span className="km-meta">
        <span className="km-ap-name">{name || "—"}</span>
        <span className="km-ap-sub">
          {resp ? <span className="km-ap-resp">{resp}</span> : null}
          {isExtern != null ? (
            <span className={"km-tag " + (isExtern ? "is-extern" : "is-intern")}>
              {isExtern ? "extern" : "intern"}
            </span>
          ) : null}
        </span>
        <button type="button" className="km-val km-ap-val" onClick={onClick}>
          {value}
        </button>
      </span>
      <button type="button" className="km-go" title="Zum Kontakt" aria-label="Zum Kontakt" onClick={onGo}>
        {I.arrowR}
      </button>
    </div>
  );
}

/** Das eigentliche Modal — lädt beim Öffnen das volle Detail nach. */
/**
 * Das Modal allein — für Listen, die ihre Auslöser selbst zeichnen (die
 * Beziehungslisten der Anlage, die auch in einer gesperrten Akte öffnen müssen).
 */
export function KontaktmoeglichkeitModal(props: Parameters<typeof KmModal>[0]) {
  return <KmModal {...props} />;
}

function KmModal({
  row,
  mode,
  onMode,
  onClose,
}: {
  row: Row;
  mode: Mode;
  onMode: (m: Mode) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { callNumber } = useTelephony();
  // Mit den schon vorhandenen Zeilendaten seeden (eigene Comms sind in der
  // Slim-Shape enthalten) → Instant-Render; Mitglieder kommen per Fetch nach.
  const [comm, setComm] = useState<Row>(row);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(false);
    apiFetch<any>("contact/" + row.kontaktId, {
      signal: ctrl.signal,
      params: { include: "relations,relations-deep" },
    })
      .then(({ data }) => setComm(mapContactRow(data)))
      .catch((e) => {
        if (e?.name !== "AbortError") setError(true);
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [row.kontaktId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isFirma = String(row.typ ?? "") === "Firma";
  // Nur tatsächlich gesetzte Social-Links (leere/entfernte ausblenden).
  const socials = (comm.socialMedia ?? []).filter((s) => (s.value ?? "").trim() !== "");
  const erreichbarkeit = (comm.erreichbarkeit ?? "").trim();

  const phones = comm.phones ?? [];
  const emails = comm.emails ?? [];
  const relPhones = comm.relationPhones ?? [];
  const relEmails = comm.relationEmails ?? [];

  // Die private Assistenz einer PERSON (PO 21.09.2026): Wer in der Firmenakte
  // beim Geschäftsführer auf den Hörer klickt, soll auch die Nummer seiner
  // Assistenz sehen — sie ist oft der eigentliche Kommunikationsweg.
  //
  // Kein zusätzlicher Abruf nötig: Von DIESEM Kontakt aus liegt die Assistenz
  // genau eine Beziehungsebene entfernt, und die lädt das Popup ohnehin schon
  // (`include=relations,relations-deep`). Nur angezeigt wurden diese Zeilen
  // bisher allein bei Firmen.
  const istAssistenz = (r: { role?: string }) => (r.role ?? "").trim() === ASSISTENZ_ROLLE;
  const assistenzPhones = relPhones.filter(istAssistenz);
  const assistenzEmails = relEmails.filter(istAssistenz);
  const zeigeAssistenz =
    !isFirma && (mode === "phone" ? assistenzPhones.length > 0 : assistenzEmails.length > 0);

  const ownTitle = isFirma ? "Firma / Zentrale" : "Kontaktdaten";
  // Dieselbe Liste wie im Kontakt-Detail, also auch derselbe Name. Hier liegt nur
  // das Rechtsform-LABEL vor (mapContactRow flacht `legalForm` auf den Text ab) —
  // der Begriff wird deshalb über den Namen aufgelöst.
  const legalFormLabel = typeof comm.legalForm === "string" ? comm.legalForm : undefined;
  const begriff = useUnternehmensBegriff(undefined, isFirma ? legalFormLabel : undefined);
  const verantTitel = verantwortungTitel(begriff);

  const ownEmpty = mode === "phone" ? phones.length === 0 : emails.length === 0;
  const relEmpty = mode === "phone" ? relPhones.length === 0 : relEmails.length === 0;

  const goTo = (contactId: string) => () => {
    onClose();
    navigate("/contacts/" + contactId);
  };

  // T-227/T-296: Mail im KMVS-Fenster statt Outlook. Kontext ist immer der
  // Kontakt, dessen Popup offen ist — auch bei Assistenz und Ansprechpartnern:
  // Verträge, Dokumente und die übrigen Adressen hängen an ihm.
  const mailSchreiben = useMailSchreiben();
  const mailen = (adresse: string) => () => {
    onClose();
    mailSchreiben(adresse, row.kontaktId);
  };

  return createPortal(
    <div className="kvp-rd kvp-rd-scrim" onClick={onClose}>
      <div className="dm km-dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {/* Kopf */}
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-ico">{mode === "phone" ? I.phone : I.mail}</div>
            <div className="dm-titles">
              <div className="dm-kicker">Kontaktmöglichkeiten</div>
              <h2>{row.displayName || "—"}</h2>
            </div>
            {socials.length > 0 && (
              <div className="km-social">
                {socials.map((sm) => (
                  <button
                    key={sm.id}
                    type="button"
                    className="km-social-ic"
                    title={(SOCIAL_LABEL[sm.type] || sm.type) + (sm.value ? ` · ${sm.value}` : "")}
                    onClick={() => sm.value && window.open(normalizeWebUrl(sm.value), "_blank", "noopener,noreferrer")}
                  >
                    <SocialIcon
                      network={sm.type === "homepage" ? "sharethis" : sm.type}
                      fgColor="#ffffff"
                      style={{ height: 26, width: 26 }}
                    />
                  </button>
                ))}
              </div>
            )}
            <button className="dm-x" type="button" onClick={onClose} aria-label="Schließen">
              {XIcon}
            </button>
          </div>
          <div className="dm-tabs">
            <button type="button" className={mode === "phone" ? "on" : ""} onClick={() => onMode("phone")}>
              Telefon
            </button>
            <button type="button" className={mode === "email" ? "on" : ""} onClick={() => onMode("email")}>
              E-Mail
            </button>
          </div>
        </div>

        {/* Inhalt */}
        <div className="dm-body">
          {error && <div className="km-empty">Kontaktdaten konnten nicht geladen werden.</div>}

          {/* Erreichbarkeit (falls hinterlegt) */}
          {erreichbarkeit && <ErreichbarkeitRow text={erreichbarkeit} />}

          {/* Eigene Kommunikation */}
          <div className="km-sech">{ownTitle}</div>
          {mode === "phone"
            ? phones.map((p) => (
                <CommRow
                  key={"p" + p.id}
                  onClick={() => callNumber(p.number)}
                  icon={commIcon(p.type)}
                  kind={TYPE_LABEL[p.type ?? ""] || "Telefon"}
                  bez={realBez(p.label)}
                  value={p.number}
                />
              ))
            : emails.map((e) => (
                <CommRow
                  key={"e" + e.id}
                  onClick={mailen(e.address)}
                  icon={I.mail}
                  kind="E-Mail"
                  bez={realBez(e.label)}
                  value={e.address}
                />
              ))}
          {ownEmpty && (
            <div className="km-empty">
              {mode === "phone" ? "Keine Telefonnummern hinterlegt." : "Keine E-Mail-Adressen hinterlegt."}
            </div>
          )}

          {/* Private Assistenz — nur bei Personen. Steht bewusst ÜBER den
              „Menschen fürs Unternehmen": Wer hier landet, sucht eine Nummer,
              und die der Assistenz ist die, unter der man ankommt. */}
          {zeigeAssistenz && (
            <>
              <div className="km-sech km-sech-2">{ASSISTENZ_ROLLE}</div>
              {mode === "phone"
                ? assistenzPhones.map((p) => (
                    <ApRow
                      key={"ap" + p.contactId + "-" + p.id}
                      onClick={() => callNumber(p.number)}
                      icon={commIcon(p.type)}
                      name={p.name}
                      resp={ASSISTENZ_ROLLE}
                      isExtern={false}
                      value={p.number}
                      onGo={goTo(p.contactId)}
                    />
                  ))
                : assistenzEmails.map((e) => (
                    <ApRow
                      key={"ae" + e.contactId + "-" + e.id}
                      onClick={mailen(e.address)}
                      icon={I.mail}
                      name={e.name}
                      resp={ASSISTENZ_ROLLE}
                      isExtern={false}
                      value={e.address}
                      onGo={goTo(e.contactId)}
                    />
                  ))}
            </>
          )}

          {/* Menschen fürs Unternehmen — nur bei Firmen */}
          {isFirma && (
            <>
              <div className="km-sech km-sech-2">{verantTitel}</div>
              {mode === "phone"
                ? relPhones.map((p) => (
                    <ApRow
                      key={"rp" + p.contactId + "-" + p.id}
                      onClick={() => callNumber(p.number)}
                      icon={commIcon(p.type)}
                      name={p.name}
                      resp={(p.func && p.func.trim()) || (p.role && p.role !== "Unbekannt" ? p.role : "")}
                      isExtern={p.isExtern}
                      value={p.number}
                      onGo={goTo(p.contactId)}
                    />
                  ))
                : relEmails.map((e) => (
                    <ApRow
                      key={"re" + e.contactId + "-" + e.id}
                      onClick={mailen(e.address)}
                      icon={I.mail}
                      name={e.name}
                      resp={(e.func && e.func.trim()) || (e.role && e.role !== "Unbekannt" ? e.role : "")}
                      isExtern={e.isExtern}
                      value={e.address}
                      onGo={goTo(e.contactId)}
                    />
                  ))}
              {relEmpty && (
                <div className="km-empty">
                  {loading
                    ? `Lade ${verantTitel} …`
                    : mode === "phone"
                      ? "Keine verknüpften Personen mit Telefonnummern."
                      : "Keine verknüpften Personen mit E-Mail-Adressen."}
                </div>
              )}
            </>
          )}
        </div>

        <div className="dm-foot">
          <button type="button" className="rd-pop-btn" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Zellen-Inhalt: zwei blaue Trigger-Icons + (bei Klick) das Modal. */
export function KontaktmoeglichkeitCell({ row }: { row: Row }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("phone");

  const openWith = (m: Mode) => (e: MouseEvent) => {
    e.stopPropagation();
    setMode(m);
    setOpen(true);
  };

  return (
    <>
      <span className="kt-comm-actions" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={"gic kt-comm" + (open ? " on" : "")}
          title="Telefonnummern"
          aria-label="Telefonnummern"
          onClick={openWith("phone")}
        >
          {I.phone}
        </button>
        <button
          type="button"
          className={"gic kt-comm" + (open ? " on" : "")}
          title="E-Mail-Adressen"
          aria-label="E-Mail-Adressen"
          onClick={openWith("email")}
        >
          {I.mail}
        </button>
      </span>
      {open && <KmModal row={row} mode={mode} onMode={setMode} onClose={() => setOpen(false)} />}
    </>
  );
}
